import classnames from "classnames";
import { useEffect, useState } from "react";
import { Col, Form, Input, Label, Row } from "reactstrap";
import { formValidation, toaster } from "../../../function";
import { ToastsStore } from "react-toasts";
import { Button, TextField, TextGroup, TextLabel } from "../../Styled";
import { ModalBox, ModalOverlay } from "../../Modals/ModalStyled";
import { requiredField, validateAttrition } from "./validation";
import "./attritionStyle.css";

const MissingAttritionModal = ({
  show,
  rows: initialRows,
  token,
  insertAttritionList,
  onSaved,
}) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setRows(initialRows || []);
      setErrors({});
      setSaving(false);
    }
  }, [show, initialRows]);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [name]: value } : row))
    );
    setErrors((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev, [index]: { ...prev[index] } };
      delete next[index][name];
      if (!Object.keys(next[index]).length) delete next[index];
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving || !rows.length) return;

    const rowErrors = {};
    rows.forEach((row, index) => {
      const customErrors = validateAttrition(row);
      if (Object.keys(customErrors).length > 0) {
        rowErrors[index] = customErrors;
        return;
      }

      const validate = formValidation(requiredField, row);
      if (!validate.formIsValid) {
        rowErrors[index] = validate.errors;
      }
    });

    if (Object.keys(rowErrors).length > 0) {
      setErrors(rowErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const payload = rows.map((row) => ({
        package_case: row.package_case,
        percentage: row.percentage,
        count: row.count,
        lead_classification: row.lead_classification,
        is_active: true,
      }));

      const res = await insertAttritionList(token, payload);

      if (!(res && res.code >= 200 && res.code < 400)) {
        const message =
          (typeof res?.message === "string" && res.message) ||
          "Failed to save attrition packages.";
        toaster(ToastsStore, "error", message);
        return;
      }

      toaster(ToastsStore, "success", "Missing attrition packages saved.");
      onSaved();
    } catch (err) {
      toaster(ToastsStore, "error", "Failed to save attrition packages.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay className={classnames("missingAttritionOverlay", { show })}>
      <ModalBox className="missingAttritionBox">
        <div className="modalContent">
          <div className="modalHeader">
            <h4 className="modalTitle">Add Missing Attrition Packages</h4>
            <p>
              {rows.length} package{rows.length === 1 ? "" : "s"} from this BOM
              {rows.length === 1 ? " is" : " are"} not in Attrition. All fields
              are required.
            </p>
          </div>

          <Form
            className="w-100 white-form missingAttritionForm"
            onSubmit={onSubmit}
          >
            <div className="missingAttritionRows">
              {rows.map((row, index) => {
                const rowError = errors[index] || {};
                return (
                  <div className="missingAttritionRow" key={row.package_case}>
                    <Row className="align-items-start">
                      <Col md="3">
                        <TextGroup
                          className={classnames({
                            error: rowError.package_case,
                          })}
                        >
                          <TextLabel>Package Name *</TextLabel>
                          <TextField
                            className="sm"
                            type="text"
                            name="package_case"
                            value={row.package_case || ""}
                            readOnly
                            disabled
                          />
                          {rowError.package_case ? (
                            <span className="error-text">
                              {rowError.package_case}
                            </span>
                          ) : null}
                        </TextGroup>
                      </Col>
                      <Col md="2">
                        <TextGroup
                          className={classnames({
                            error: rowError.percentage,
                          })}
                        >
                          <TextLabel>Percentage (%) *</TextLabel>
                          <TextField
                            className="sm"
                            type="text"
                            name="percentage"
                            placeholder="Enter percentage"
                            value={
                              row.percentage === 0 || row.percentage === "0"
                                ? row.percentage
                                : row.percentage || ""
                            }
                            onChange={(e) => handleChange(index, e)}
                          />
                          {rowError.percentage ? (
                            <span className="error-text">
                              {rowError.percentage}
                            </span>
                          ) : null}
                        </TextGroup>
                      </Col>
                      <Col md="2">
                        <TextGroup
                          className={classnames({ error: rowError.count })}
                        >
                          <TextLabel>Count *</TextLabel>
                          <TextField
                            className="sm"
                            type="number"
                            name="count"
                            placeholder="Count"
                            value={
                              row.count === 0 || row.count === "0"
                                ? row.count
                                : row.count || ""
                            }
                            onChange={(e) => handleChange(index, e)}
                          />
                          {rowError.count ? (
                            <span className="error-text">{rowError.count}</span>
                          ) : null}
                        </TextGroup>
                      </Col>
                      <Col md="5">
                        <TextGroup>
                          <TextLabel>Lead Classification *</TextLabel>
                          <div className="missingAttritionRadios">
                            <Label className="customRadio d-inline-block">
                              <Input
                                type="radio"
                                name={`lead_classification_${index}`}
                                value="lead"
                                checked={row.lead_classification === "lead"}
                                onChange={() =>
                                  handleChange(index, {
                                    target: {
                                      name: "lead_classification",
                                      value: "lead",
                                    },
                                  })
                                }
                              />
                              <span className="span-text">Lead</span>
                            </Label>
                            <Label className="customRadio d-inline-block">
                              <Input
                                type="radio"
                                name={`lead_classification_${index}`}
                                value="leadless"
                                checked={
                                  row.lead_classification === "leadless"
                                }
                                onChange={() =>
                                  handleChange(index, {
                                    target: {
                                      name: "lead_classification",
                                      value: "leadless",
                                    },
                                  })
                                }
                              />
                              <span className="span-text">Leadless</span>
                            </Label>
                            <Label className="customRadio d-inline-block">
                              <Input
                                type="radio"
                                name={`lead_classification_${index}`}
                                value="unknown"
                                checked={
                                  row.lead_classification === "unknown"
                                }
                                onChange={() =>
                                  handleChange(index, {
                                    target: {
                                      name: "lead_classification",
                                      value: "unknown",
                                    },
                                  })
                                }
                              />
                              <span className="span-text">Unknown</span>
                            </Label>
                          </div>
                          {rowError.lead_classification ? (
                            <span className="error-text">
                              {rowError.lead_classification}
                            </span>
                          ) : null}
                        </TextGroup>
                      </Col>
                    </Row>
                  </div>
                );
              })}
            </div>

            <TextGroup className="missingAttritionActions">
              <Button
                className="importBtn blue"
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </TextGroup>
          </Form>
        </div>
      </ModalBox>
    </ModalOverlay>
  );
};

export default MissingAttritionModal;
